import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Sparkles, KeyRound, Headset, BadgePercent, CalendarDays, Users as UsersIcon } from "lucide-react";
import { MaskedLines, FadeUp } from "@/components/motion";
import { Container, SectionHeading } from "@/components/Section";
import PropertyCard from "@/components/PropertyCard";
import useProperties from "@/hooks/useProperties";
import { UPLISTING_BASE } from "@/data/content";

const TRUST = [
  { icon: Sparkles, t: "Hotel-standard cleaning", d: "Professional turnovers and fresh linen before every stay." },
  { icon: KeyRound, t: "Simple self check-in", d: "Clear instructions and lockbox access — arrive on your schedule." },
  { icon: Headset, t: "Local support", d: "A Dublin-based team on hand for anything during your stay." },
  { icon: BadgePercent, t: "Best rate, direct", d: "No platform fees when you book directly with FieldGreen." },
];

export default function StayWithUs() {
  const properties = useProperties();
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [guests, setGuests] = useState("2");

  const params = new URLSearchParams();
  if (checkIn) params.set("checkIn", checkIn);
  if (checkOut) params.set("checkOut", checkOut);
  if (guests) params.set("guests", guests);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  const searchUrl = `${UPLISTING_BASE}/${suffix}`;

  const fieldCls =
    "w-full rounded-lg border border-white/15 bg-white/10 px-4 py-3 text-sm text-cream transition-colors duration-300 focus:border-sage [color-scheme:dark]";

  return (
    <main data-testid="stay-page">
      <section className="noise-dark relative overflow-hidden bg-forest pb-24 pt-44 text-cream sm:pb-28 sm:pt-52">
        <div className="pointer-events-none absolute -top-40 -right-40 h-[480px] w-[480px] rounded-full bg-forest-700/30 blur-[140px]" />
        <Container className="relative z-10">
          <FadeUp>
            <p className="eyebrow text-sage">Stay with FieldGreen · Book direct</p>
          </FadeUp>
          <MaskedLines
            className="mt-6 max-w-4xl"
            lineClassName="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.06]"
            lines={[
              <span key="1">Dublin stays,</span>,
              <span key="2" className="italic text-sage-light">professionally managed.</span>,
            ]}
          />
          <FadeUp delay={0.35}>
            <p className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-cream/70">
              Every FieldGreen property is prepared to a hotel standard and bookable direct — live availability,
              the best rate, and no platform fees. Choose your stay below.
            </p>
          </FadeUp>
        </Container>
      </section>

      <section className="bg-cream py-24 sm:py-32">
        <Container>
          <FadeUp>
            <div className="rounded-2xl border border-forest/20 bg-forest p-6 sm:p-7 noise-dark" data-testid="date-search-bar">
              <div className="relative z-10 grid items-end gap-4 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_auto]">
                <div>
                  <label htmlFor="ds-checkin" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-sage">
                    Check-in
                  </label>
                  <div className="relative">
                    <CalendarDays className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-sage/70" />
                    <input
                      id="ds-checkin"
                      type="date"
                      value={checkIn}
                      min={new Date().toISOString().split("T")[0]}
                      onChange={(e) => setCheckIn(e.target.value)}
                      data-testid="date-search-checkin-input"
                      className={`${fieldCls} pl-10`}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="ds-checkout" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-sage">
                    Check-out
                  </label>
                  <div className="relative">
                    <CalendarDays className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-sage/70" />
                    <input
                      id="ds-checkout"
                      type="date"
                      value={checkOut}
                      min={checkIn || new Date().toISOString().split("T")[0]}
                      onChange={(e) => setCheckOut(e.target.value)}
                      data-testid="date-search-checkout-input"
                      className={`${fieldCls} pl-10`}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="ds-guests" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-sage">
                    Guests
                  </label>
                  <div className="relative">
                    <UsersIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-sage/70" />
                    <select
                      id="ds-guests"
                      value={guests}
                      onChange={(e) => setGuests(e.target.value)}
                      data-testid="date-search-guests-select"
                      className={`${fieldCls} appearance-none pl-10`}
                    >
                      {[1, 2, 3, 4, 5, 6].map((n) => (
                        <option key={n} value={n} className="text-charcoal">
                          {n} guest{n > 1 ? "s" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <a
                  href={searchUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="date-search-button"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-cream px-7 py-3.5 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-sage"
                >
                  Search Availability
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            </div>
          </FadeUp>
          <div className="mt-12 grid gap-10 md:grid-cols-2 xl:grid-cols-3" data-testid="properties-grid">
            {properties.map((p, i) => (
              <PropertyCard key={p.id} p={p} i={i} suffix={suffix} />
            ))}
          </div>
          <FadeUp delay={0.2}>
            <p className="mt-12 max-w-2xl text-xs leading-relaxed text-charcoal/50" data-testid="booking-sync-note">
              Availability and nightly pricing are always live — when you book, you'll complete your reservation
              securely on FieldGreen's direct booking site, synced in real time with our property calendar.
            </p>
          </FadeUp>
        </Container>
      </section>

      <section className="bg-bone py-24 sm:py-28">
        <Container>
          <div className="grid gap-px overflow-hidden rounded-2xl border border-forest/10 bg-forest/10 sm:grid-cols-2 lg:grid-cols-4">
            {TRUST.map(({ icon: Icon, t, d }, i) => (
              <FadeUp key={t} delay={i * 0.06} className="h-full">
                <div data-testid={`stay-trust-${i}`} className="flex h-full flex-col bg-bone p-8">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-forest text-sage">
                    <Icon className="h-4 w-4" />
                  </span>
                  <h3 className="mt-5 font-serif text-xl font-medium text-forest">{t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-charcoal/60">{d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <div className="mt-10 flex flex-wrap items-center justify-between gap-6 rounded-2xl border border-forest/12 bg-cream p-8">
              <p className="max-w-xl font-serif text-2xl font-medium text-forest">
                Staying 21 days or more? <span className="italic text-forest-700">Ask about corporate rates.</span>
              </p>
              <Link
                to="/corporate-stays"
                data-testid="stay-corporate-link"
                className="group inline-flex items-center gap-2 rounded-full bg-forest px-6 py-3.5 text-sm font-semibold text-cream transition-colors duration-300 hover:bg-forest-700"
              >
                Corporate Stays
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </FadeUp>
        </Container>
      </section>

      <section className="noise-dark bg-forest py-24 text-cream sm:py-28">
        <Container className="relative z-10 flex flex-col items-start justify-between gap-10 lg:flex-row lg:items-center">
          <div className="max-w-xl">
            <FadeUp>
              <p className="eyebrow text-sage">For property owners</p>
              <h2 className="mt-5 font-serif text-4xl sm:text-5xl font-medium">Own a property that could look like this?</h2>
              <p className="mt-5 text-base leading-relaxed text-cream/65">
                This is how FieldGreen presents, prices and books the properties we manage — across Airbnb, major
                channels and direct. See what yours could earn.
              </p>
            </FadeUp>
          </div>
          <FadeUp delay={0.15}>
            <Link
              to="/assessment"
              data-testid="stay-owner-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-cream px-7 py-4 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-sage"
            >
              Get a Property Assessment
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </FadeUp>
        </Container>
      </section>
    </main>
  );
}
